import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  Check,
  Clock,
  ExternalLink,
  Eye,
  FileQuestion,
  Home,
  Image as ImageIcon,
  Info,
  Link2,
  Loader2,
  Pause,
  Play,
  Rocket,
  Settings,
  Star,
  X,
  XCircle,
} from 'lucide-react';
import { useTenant } from '../context/TenantContext';
import { getWebinarStatus, updateLaunchStatus, type WebinarStatusData } from '../lib/webinarStatus';
import { calculateEmailDates, LANDING_PAGE_THEMES } from '../lib/launchPlanner';
import { getProfile, type AdvisorProfile } from '../lib/advisorProfile';

import { formatDate, parseLocalDate, isPast } from '../lib/dateUtils';

// ─── Local Helpers ──────────────────────────────────────────────────────────

function formatDateShort(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function daysUntil(dateStr: string): number {
  const target = parseLocalDate(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

function isToday(d: Date): boolean {
  const today = new Date();
  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
}

function getThemeLabel(value: string): string {
  const theme = LANDING_PAGE_THEMES.find((t) => t.value === value);
  return theme ? theme.label : value;
}

// ─── Status Badge ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'launched':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-green-500/15 text-green-400 border border-green-500/25">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
          </span>
          Live
        </span>
      );
    case 'paused':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/25">
          <Pause className="w-3 h-3" />
          Paused
        </span>
      );
    case 'cancelled':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-red-500/15 text-red-400 border border-red-500/25 line-through decoration-red-400/50">
          Cancelled
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-slate-500/15 text-slate-400 border border-slate-500/25">
          Draft
        </span>
      );
  }
}

// ─── Confirmation Modal ──────────────────────────────────────────────────────

function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  confirmColor,
  confirmIcon: ConfirmIcon,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: 'amber' | 'green' | 'red';
  confirmIcon: React.ElementType;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const colorMap = {
    amber: {
      iconBg: 'bg-amber-500/10',
      iconText: 'text-amber-400',
      button: 'bg-amber-600 hover:bg-amber-500',
    },
    green: {
      iconBg: 'bg-green-500/10',
      iconText: 'text-green-400',
      button: 'bg-green-600 hover:bg-green-500',
    },
    red: {
      iconBg: 'bg-red-500/10',
      iconText: 'text-red-400',
      button: 'bg-red-600 hover:bg-red-500',
    },
  };
  const colors = colorMap[confirmColor];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-slate-900 border border-slate-700/50 rounded-2xl p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4 mb-6">
              <div
                className={`w-10 h-10 rounded-full ${colors.iconBg} flex items-center justify-center flex-shrink-0`}
              >
                <AlertTriangle className={`w-5 h-5 ${colors.iconText}`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
                <p className="text-sm text-slate-400">{message}</p>
              </div>
              <button
                onClick={onCancel}
                className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm font-medium transition-colors border border-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={loading}
                className={`inline-flex items-center gap-2 px-4 py-2 ${colors.button} text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50`}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ConfirmIcon className="w-4 h-4" />
                )}
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Card Wrapper ────────────────────────────────────────────────────────────

function StatusCard({
  index,
  icon: Icon,
  title,
  children,
}: {
  index: number;
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
      className="relative group bg-slate-900/50 backdrop-blur-xl rounded-2xl p-6 transition-all duration-300 border border-slate-700/50 hover:border-slate-600/60 hover:shadow-lg hover:shadow-primary-500/5"
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary-500/30 to-transparent" />

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-primary-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
      </div>

      {children}
    </motion.div>
  );
}

import { ImagePreview } from '../components/ui/ImagePreview';

// ─── Main Component ──────────────────────────────────────────────────────────

export function WebinarStatus() {
  const navigate = useNavigateWithParams();
  const { id: webinarId } = useParams<{ id: string }>();
  const { locationId } = useTenant();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [webinar, setWebinar] = useState<WebinarStatusData | null>(null);
  const [profile, setProfile] = useState<AdvisorProfile | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Confirmation modal state
  const [confirmAction, setConfirmAction] = useState<'pause' | 'resume' | 'cancel' | null>(null);

  // ─── Load data ──────────────────────────────────────────────────────────────

  const fetchWebinar = useCallback(async () => {
    if (!webinarId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    try {
      const data = await getWebinarStatus(webinarId);
      if (!data) {
        setNotFound(true);
        return;
      }
      setWebinar(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [webinarId]);

  useEffect(() => {
    fetchWebinar();
  }, [fetchWebinar]);

  useEffect(() => {
    async function loadProfile() {
      try {
        const p = await getProfile(locationId);
        setProfile(p);
      } catch {
        // ignore
      }
    }
    loadProfile();
  }, [locationId]);

  // ─── Toast ──────────────────────────────────────────────────────────────────

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const handleStatusChange = async (newStatus: 'launched' | 'paused' | 'cancelled') => {
    if (!webinarId) return;
    setActionLoading(true);
    try {
      await updateLaunchStatus(webinarId, newStatus);
      await fetchWebinar();
      const messages = {
        launched: 'Webinar resumed successfully',
        paused: 'Webinar paused — scheduled emails are on hold',
        cancelled: 'Webinar cancelled — all remaining emails stopped',
      };
      showToast('success', messages[newStatus]);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  // ─── Email timeline data ───────────────────────────────────────────────────

  const emailTimeline = useMemo(() => {
    if (!webinar?.webinar_date) return null;
    const d = parseLocalDate(webinar.webinar_date);
    if (isNaN(d.getTime())) return null;
    const dates = calculateEmailDates(d);
    return [
      { key: 'marketing1', label: 'Email 1 — The Mistake Hook', date: dates.marketing1, variant: 'blue' as const },
      { key: 'marketing2', label: 'Email 2 — The Origin / Trust', date: dates.marketing2, variant: 'blue' as const },
      { key: 'marketing3', label: 'Email 3 — The Logic / Villain', date: dates.marketing3, variant: 'blue' as const },
      { key: 'marketing4', label: 'Email 4 — The Goodie Bag', date: dates.marketing4, variant: 'blue' as const },
      { key: 'marketing5', label: 'Email 5 — Behind the Scenes', date: dates.marketing5, variant: 'blue' as const },
      { key: 'webinarDay', label: 'Webinar Day', date: d, variant: 'center' as const },
      { key: 'marketing6', label: 'Email 6 — The Agenda (morning)', date: dates.marketing6, variant: 'blue' as const },
      { key: 'marketing7', label: 'Email 7 — Starting in 60 min', date: dates.marketing7, variant: 'blue' as const },
      { key: 'marketing8', label: 'Email 8 — WE ARE LIVE', date: dates.marketing8, variant: 'blue' as const },
      { key: 'followup1', label: 'Follow-Up 1 — Recap & Shift (4hrs post)', date: dates.followup1, variant: 'purple' as const },
      { key: 'followup2', label: 'Follow-Up 2 — The Case Study', date: dates.followup2, variant: 'purple' as const },
      { key: 'followup3', label: 'Follow-Up 3 — The Spear', date: dates.followup3, variant: 'purple' as const },
    ];
  }, [webinar?.webinar_date]);

  // ─── Days until webinar ─────────────────────────────────────────────────────

  const daysLabel = useMemo(() => {
    if (!webinar?.webinar_date) return null;
    const days = daysUntil(webinar.webinar_date);
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days > 0) return `${days} days away`;
    if (days === -1) return '1 day ago';
    return `${Math.abs(days)} days ago`;
  }, [webinar?.webinar_date]);

  const launchStatus = webinar?.launch_status || 'draft';
  const isCancelled = launchStatus === 'cancelled';
  const isPaused = launchStatus === 'paused';

  // ─── Not found ──────────────────────────────────────────────────────────────

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
              <FileQuestion className="w-8 h-8 text-slate-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Webinar Not Found</h2>
            <p className="text-slate-400 mb-6">
              We couldn't find this webinar. It may have been deleted or the link is invalid.
            </p>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-500 text-white font-medium rounded-lg transition-colors"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Loading ────────────────────────────────────────────────────────────────

  if (loading || !webinar) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
          <p className="text-slate-400">Loading webinar status...</p>
        </div>
      </div>
    );
  }

  // ─── Email status for each node ─────────────────────────────────────────────

  function getEmailStatus(date: Date): {
    label: string;
    dotClass: string;
    labelClass: string;
    showPause?: boolean;
    strikethrough?: boolean;
  } {
    if (isCancelled) {
      if (isPast(date)) {
        return {
          label: 'Sent',
          dotClass: 'bg-green-500',
          labelClass: 'text-green-400',
        };
      }
      return {
        label: 'Cancelled',
        dotClass: 'bg-red-500/50',
        labelClass: 'text-red-400 line-through',
        strikethrough: true,
      };
    }

    if (isToday(date)) {
      return {
        label: 'Sending Today',
        dotClass: 'bg-green-500',
        labelClass: 'text-green-400',
        showPause: isPaused,
      };
    }

    if (isPast(date)) {
      return {
        label: isPaused ? 'Sent (before pause)' : 'Sent',
        dotClass: 'bg-green-500',
        labelClass: 'text-green-400',
      };
    }

    // Future
    if (isPaused) {
      return {
        label: 'On Hold',
        dotClass: 'bg-amber-500/60',
        labelClass: 'text-amber-400',
        showPause: true,
      };
    }

    return {
      label: 'Scheduled',
      dotClass: 'bg-primary-500',
      labelClass: 'text-primary-400',
    };
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen p-4 md:p-8 pb-32">
      <div className="w-full max-w-3xl mx-auto space-y-6">
        {/* ── C3 Info Banner ───────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 px-4 py-3 bg-primary-500/5 border border-primary-500/15 rounded-xl text-sm text-primary-300/80"
        >
          <Info className="w-4 h-4 flex-shrink-0 text-primary-400/60" />
          <span>
            Status tracking is active. GHL deployment will be enabled in an upcoming update.
          </span>
        </motion.div>

        {/* ── Page Header ──────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="text-center space-y-4 pt-2"
        >
          {/* Badge */}
          <div className="relative inline-block">
            <div className="absolute inset-0 rounded-full bg-primary-500/20 blur-2xl animate-pulse" />
            <div className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20">
              <Activity className="w-4 h-4 text-primary-400" />
              <span className="text-sm text-primary-300 font-medium">Mission Control</span>
            </div>
          </div>

          {/* Title + status */}
          <div className="space-y-2">
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              {webinar.title || 'Untitled Webinar'}
            </h1>
            {webinar.subtitle && (
              <p className="text-slate-400 text-lg">{webinar.subtitle}</p>
            )}
            <div className="flex items-center justify-center gap-3 pt-1">
              <StatusBadge status={launchStatus} />
            </div>
          </div>

          {/* Quick stats row */}
          <div className="flex items-center justify-center gap-6 text-sm">
            {webinar.webinar_date && (
              <div className="flex items-center gap-1.5 text-slate-400">
                <CalendarDays className="w-4 h-4 text-slate-500" />
                <span>{formatDate(parseLocalDate(webinar.webinar_date))}</span>
              </div>
            )}
            {webinar.webinar_time && (
              <div className="flex items-center gap-1.5 text-slate-400">
                <Clock className="w-4 h-4 text-slate-500" />
                <span>{webinar.webinar_time}</span>
              </div>
            )}
            {daysLabel && (
              <span
                className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  daysUntil(webinar.webinar_date) >= 0
                    ? 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                    : 'bg-slate-700/50 text-slate-400'
                }`}
              >
                {daysLabel}
              </span>
            )}
          </div>

          {/* Registration link */}
          {webinar.registration_link && (
            <a
              href={webinar.registration_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              <Link2 className="w-3.5 h-3.5" />
              Registration Page
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </motion.div>

        {/* ── Card 1: Email Timeline ───────────────────────────────────────── */}
        <StatusCard index={0} icon={CalendarDays} title="Email Timeline">
          {emailTimeline ? (
            <div className="relative pl-1">
              {/* Connecting line */}
              <div className="absolute left-[12px] top-3 bottom-3 w-px bg-gradient-to-b from-primary-500/60 via-slate-600/40 to-purple-500/60" />

              {emailTimeline.map((node) => {
                const isCenter = node.variant === 'center';

                if (isCenter) {
                  return (
                    <div key={node.key} className="relative flex items-start gap-4 pb-5">
                      <div className="relative z-10 flex-shrink-0 mt-0.5">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-500 to-purple-500 flex items-center justify-center shadow-lg shadow-primary-500/30">
                          <Star className="w-3.5 h-3.5 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 flex flex-wrap items-baseline gap-3">
                        <span className="text-base font-medium text-white">{node.label}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary-500/20 text-primary-300 font-medium">
                          {formatDate(node.date)}
                        </span>
                      </div>
                    </div>
                  );
                }

                const emailStatus = getEmailStatus(node.date);

                return (
                  <div key={node.key} className="relative flex items-start gap-4 pb-5 last:pb-0">
                    {/* Dot */}
                    <div className="relative z-10 flex-shrink-0 mt-0.5">
                      {emailStatus.label === 'Sending Today' && !isPaused ? (
                        <div className="relative ml-1">
                          <span className="absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75 animate-ping" />
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-green-400" />
                        </div>
                      ) : emailStatus.label === 'Sent' || emailStatus.label === 'Sent (before pause)' ? (
                        <div className="w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center ml-0.5">
                          <Check className="w-3 h-3 text-green-400" />
                        </div>
                      ) : emailStatus.showPause ? (
                        <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center ml-0.5">
                          <Pause className="w-2.5 h-2.5 text-amber-400" />
                        </div>
                      ) : emailStatus.strikethrough ? (
                        <div className="w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center ml-0.5">
                          <X className="w-3 h-3 text-red-400" />
                        </div>
                      ) : (
                        <div
                          className={`w-3 h-3 rounded-full mt-1 ml-1.5 ${
                            node.variant === 'blue'
                              ? 'bg-primary-500 shadow-sm shadow-primary-500/50'
                              : 'bg-purple-500 shadow-sm shadow-purple-500/50'
                          }`}
                        />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-wrap items-baseline gap-3">
                      <span
                        className={`text-sm font-medium ${
                          emailStatus.strikethrough
                            ? 'text-slate-500 line-through'
                            : node.variant === 'blue'
                            ? 'text-primary-300'
                            : 'text-purple-300'
                        }`}
                      >
                        {node.label}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">
                        {formatDateShort(node.date)}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border ${
                          emailStatus.label === 'Sent' || emailStatus.label === 'Sent (before pause)'
                            ? 'bg-green-500/10 border-green-500/20 text-green-400'
                            : emailStatus.label === 'Sending Today'
                            ? 'bg-green-500/10 border-green-500/20 text-green-400'
                            : emailStatus.label === 'On Hold'
                            ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                            : emailStatus.label === 'Cancelled'
                            ? 'bg-red-500/10 border-red-500/20 text-red-400'
                            : 'bg-primary-500/10 border-primary-500/20 text-primary-400'
                        }`}
                      >
                        {emailStatus.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic py-4 text-center border border-dashed border-slate-700/50 rounded-xl">
              No webinar date configured
            </div>
          )}
        </StatusCard>

        {/* ── Card 2: Quick Actions ────────────────────────────────────────── */}
        <StatusCard index={1} icon={Rocket} title="Quick Actions">
          <div className="space-y-4">
            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              {/* Pause — only if launched */}
              {launchStatus === 'launched' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setConfirmAction('pause')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-500/30 rounded-xl text-sm font-medium transition-all shadow-lg shadow-amber-500/5"
                >
                  <Pause className="w-4 h-4" />
                  Pause Campaign
                </motion.button>
              )}

              {/* Resume — only if paused */}
              {launchStatus === 'paused' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setConfirmAction('resume')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/30 rounded-xl text-sm font-medium transition-all shadow-lg shadow-green-500/5"
                >
                  <Play className="w-4 h-4" />
                  Resume Campaign
                </motion.button>
              )}

              {/* Cancel — if launched or paused */}
              {(launchStatus === 'launched' || launchStatus === 'paused') && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setConfirmAction('cancel')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600/15 hover:bg-red-600/25 text-red-400 border border-red-500/25 rounded-xl text-sm font-medium transition-all"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel Campaign
                </motion.button>
              )}

              {/* Back to Launch Planner — if draft */}
              {launchStatus === 'draft' && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(`/launch/${webinarId}`)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600/20 hover:bg-primary-600/30 text-primary-400 border border-primary-500/30 rounded-xl text-sm font-medium transition-all shadow-lg shadow-primary-500/5"
                >
                  <Settings className="w-4 h-4" />
                  Back to Launch Planner
                </motion.button>
              )}

              {/* Cancelled state message */}
              {launchStatus === 'cancelled' && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <XCircle className="w-4 h-4" />
                  This campaign has been cancelled. No further actions available.
                </div>
              )}
            </div>

            {/* Navigation links */}
            <div className="flex items-center gap-4 pt-2 border-t border-slate-800">
              <button
                onClick={() => navigate(`/results/${webinarId}`)}
                className="inline-flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                View Results
              </button>
              {launchStatus !== 'draft' && (
                <button
                  onClick={() => navigate(`/launch/${webinarId}`)}
                  className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-300 transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Launch Config
                </button>
              )}
            </div>
          </div>
        </StatusCard>

        {/* ── Card 3: Webinar Summary ──────────────────────────────────────── */}
        <StatusCard index={2} icon={Info} title="Webinar Summary">
          <div className="space-y-4">
            {/* Summary grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Landing page theme */}
              {webinar.landing_page_theme && (
                <div className="space-y-1">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Landing Page Theme</span>
                  <p className="text-sm text-slate-200">{getThemeLabel(webinar.landing_page_theme)}</p>
                </div>
              )}

              {/* Registration link */}
              {webinar.registration_link && (
                <div className="space-y-1">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Registration Link</span>
                  <a
                    href={webinar.registration_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors truncate"
                  >
                    <Link2 className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{webinar.registration_link}</span>
                  </a>
                </div>
              )}

              {/* Profile info */}
              {profile?.advisor_name && (
                <div className="space-y-1">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Presenter</span>
                  <p className="text-sm text-slate-200">{profile.advisor_name}</p>
                  {profile.company_name && (
                    <p className="text-xs text-slate-400">{profile.company_name}</p>
                  )}
                </div>
              )}

              {/* Webinar date + time */}
              {webinar.webinar_date && (
                <div className="space-y-1">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Scheduled</span>
                  <p className="text-sm text-slate-200">
                    {formatDate(parseLocalDate(webinar.webinar_date))}
                    {webinar.webinar_time ? ` at ${webinar.webinar_time}` : ''}
                  </p>
                </div>
              )}
            </div>

            {/* Presenter image */}
            {webinar.presenter_image && (
              <div className="flex items-center gap-4 pt-2 border-t border-slate-800">
                <ImagePreview url={webinar.presenter_image} size={56} />
                <div className="space-y-0.5">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Presenter Image</span>
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <ImageIcon className="w-3 h-3" />
                    Configured
                  </div>
                </div>
              </div>
            )}

            {/* Edit link */}
            <div className="pt-2 border-t border-slate-800">
              <button
                onClick={() => navigate(`/launch/${webinarId}`)}
                className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-300 transition-colors"
              >
                <Settings className="w-3.5 h-3.5" />
                Edit in Launch Planner
              </button>
            </div>
          </div>
        </StatusCard>

        {/* ── Bottom Nav ───────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex items-center justify-between pt-4 pb-8"
        >
          <button
            onClick={() => navigate('/history')}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to History
          </button>

          <button
            onClick={() => navigate(`/results/${webinarId}`)}
            className="inline-flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300 transition-colors"
          >
            <Eye className="w-4 h-4" />
            View Results
          </button>
        </motion.div>
      </div>

      {/* ── Confirmation Modals ────────────────────────────────────────────── */}
      <ConfirmModal
        open={confirmAction === 'pause'}
        title="Pause Campaign"
        message="This will pause all scheduled emails. You can resume anytime. Emails that have already been sent will not be affected."
        confirmLabel="Pause Campaign"
        confirmColor="amber"
        confirmIcon={Pause}
        loading={actionLoading}
        onConfirm={() => handleStatusChange('paused')}
        onCancel={() => setConfirmAction(null)}
      />

      <ConfirmModal
        open={confirmAction === 'resume'}
        title="Resume Campaign"
        message="This will resume scheduled emails from where they left off. Any emails whose send dates have passed while paused will be sent as soon as possible."
        confirmLabel="Resume Campaign"
        confirmColor="green"
        confirmIcon={Play}
        loading={actionLoading}
        onConfirm={() => handleStatusChange('launched')}
        onCancel={() => setConfirmAction(null)}
      />

      <ConfirmModal
        open={confirmAction === 'cancel'}
        title="Cancel Campaign"
        message="Are you sure? This will cancel all remaining scheduled emails and cannot be undone. Emails that have already been sent will not be affected."
        confirmLabel="Cancel Campaign"
        confirmColor="red"
        confirmIcon={XCircle}
        loading={actionLoading}
        onConfirm={() => handleStatusChange('cancelled')}
        onCancel={() => setConfirmAction(null)}
      />

      {/* ── Toast ──────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-6 left-1/2 px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg z-50 ${
              toast.type === 'success'
                ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-300'
                : 'bg-red-500/20 border border-red-500/30 text-red-300'
            }`}
          >
            {toast.type === 'success' ? (
              <Check className="w-4 h-4" />
            ) : (
              <AlertTriangle className="w-4 h-4" />
            )}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
